import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Dropdown,
  Form,
  InputGroup,
  ListGroupItem,
  Modal,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import * as yup from 'yup'
import {
  EmailShareButton,
  FacebookShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share'

import { IRegistration } from '../../../store/registrations/interfaces'
import { updateRegistration } from '../../../store/registrations/registrations'
import { RootState } from '../../../store'
import { AddValidatorFunc } from '../../../hooks/validators'
import { addToast } from '../../../store/misc/misc'
import { ToastType } from '../../../store/misc/interfaces'
import { useAppDispatch } from '../../../hooks'

const fetchGroupcode = async (baseUrl: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/form/generate/friendgroupcodes/`)
    return (await response.json()) as string
  } catch (error: unknown) {
    return undefined
  }
}

const copyToClipboard = async (str: string) => {
  try {
    await navigator.clipboard.writeText(str)
    return true
  } catch {
    const el = document.createElement('textarea')
    el.value = str
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    return true
  }
}

const ShareButtons = ({ code, registration }: { code: string; registration: IRegistration }) => {
  const { data: program } = useSelector((state: RootState) => state.program)
  const { baseUrl, programId } = useSelector((state: RootState) => state.misc)
  const dispatch = useAppDispatch()

  const programUrl = `${baseUrl}/anmeldung/${programId}/`
  const shareText = `Komm in meine Gruppe für "${registration.event.name}" in ${program.name}! Verwende den Code: ${code} bei der Anmeldung.`

  const handleCopy = async () => {
    const success = await copyToClipboard(code)
    if (success) {
      dispatch(addToast({
        type: ToastType.Success,
        title: 'Kopiert!',
        body: 'Gruppencode wurde in die Zwischenablage kopiert.'
      }))
    }
  }

  const shareButtons = [
    { icon: 'envelope' as IconProp, label: 'E-Mail', component: EmailShareButton, props: { url: programUrl, body: shareText } },
    { icon: ['fab', 'whatsapp'] as IconProp, label: 'WhatsApp', component: WhatsappShareButton, props: { url: programUrl, title: shareText } },
    { icon: ['fab', 'telegram'] as IconProp, label: 'Telegram', component: TelegramShareButton, props: { url: programUrl, title: shareText } },
    { icon: ['fab', 'facebook'] as IconProp, label: 'Facebook', component: FacebookShareButton, props: { url: programUrl, quote: shareText } },
    { icon: ['fab', 'x-twitter'] as IconProp, label: 'X/Twitter', component: TwitterShareButton, props: { url: programUrl, title: shareText } },
  ]

  return (
    <Dropdown.Menu>
      <Dropdown.Item onClick={handleCopy}>
        <FontAwesomeIcon icon='copy' className='mr-2' />
        Code kopieren
      </Dropdown.Item>
      <Dropdown.Divider />
      {shareButtons.map(({ icon, label, component: ShareComponent, props }) => (
        <Dropdown.Item key={label} className='p-0'>
          <ShareComponent {...props} className='w-100 text-start border-0 bg-transparent p-2'>
            <FontAwesomeIcon icon={icon} className='mr-2' />
            {label}
          </ShareComponent>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  )
}

const GroupCode = ({
                     registration,
                     editable,
                     strict_mode,
                     setError,
                     addValidator
                   }: {
  registration: IRegistration
  editable: boolean
  strict_mode: boolean
  setError: (prevState: boolean) => void
  addValidator?: AddValidatorFunc
}) => {
  const dispatch = useAppDispatch()
  const { baseUrl } = useSelector((state: RootState) => state.misc)
  const { loadedViaCode } = useSelector((state: RootState) => state.misc)
  const { originalRegistrationIds } = useSelector((state: RootState) => state.participant.data)

  const [showModal, setShowModal] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    register,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: { groupCode: registration.groupCode },
    resolver: yupResolver(yup.object().shape({
      groupCode: yup.string().max(50, 'Gruppencode ist zu lang'),
    })),
  })

  const lastError = useRef(errors.groupCode)
  const isOriginalReg = originalRegistrationIds?.includes(registration.event.event_id)
  const isDisabled = !editable || (loadedViaCode && isOriginalReg)
  const currentCode = watch('groupCode')
  const hasCode = currentCode && currentCode.length > 0

  useEffect(() => {
    if (errors.groupCode !== lastError.current) {
      setError(!!errors.groupCode)
      lastError.current = errors.groupCode

      if (editable && addValidator && !errors.groupCode) {
        addValidator(`groupCode-reg-${registration.event.event_id}`, {
          trigger,
          formName: 'GroupCode'
        })
      }
    }
  }, [errors.groupCode, editable, addValidator, registration.event.event_id, setError, trigger])

  const handleGenerateCode = async () => {
    setIsGenerating(true)
    try {
      const groupCode = await fetchGroupcode(baseUrl)
      if (groupCode) {
        setValue('groupCode', groupCode)
        dispatch(updateRegistration({
          eventId: registration.event.event_id,
          groupCode,
        }))
      } else {
        throw new Error('Kein Code erhalten')
      }
    } catch {
      dispatch(addToast({
        type: ToastType.Danger,
        title: 'Fehler',
        body: 'Der Gruppencode konnte nicht generiert werden.',
      }))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCodeChange = () => {
    dispatch(updateRegistration({
      eventId: registration.event.event_id,
      ...getValues(),
    }))
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const modeInfo = {
    icon: (strict_mode ? 'users' : 'user-friends') as IconProp,
    text: strict_mode ? 'Alle oder niemand' : 'Gruppenwunsch',
    variant: strict_mode ? 'warning' : 'info',
  }

  return (
    <>
      <ListGroupItem className='py-2'>
        {/* Compact Header Row */}
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-2 flex-grow-1'>
            <FontAwesomeIcon
              icon={modeInfo.icon}
              className='text-muted mr-2'
            />
            <span className='fw-bold'>Freundesgruppe</span>

            {/* Desktop: Show code inline when collapsed */}
            {!isExpanded && hasCode && editable && (
              <span className='d-none d-lg-inline'>
                <small className='text-muted mx-1'>Code:</small>
                <code className='ms-1'>{currentCode}</code>
              </span>
            )}
          </div>

          <div className='d-flex align-items-center gap-1'>
            {/* Help Button */}
            <Button
              variant='link'
              className='p-1 text-muted'
              onClick={() => setShowModal(true)}
              title='Hilfe'
            >
              <FontAwesomeIcon icon='question-circle' />
            </Button>

            {/* Expand/Collapse Toggle */}
            {editable && (
              <Button
                variant='link'
                className='p-1'
                onClick={toggleExpanded}
                title={isExpanded ? 'Einklappen' : 'Bearbeiten'}
              >
                <FontAwesomeIcon
                  icon={isExpanded ? 'chevron-up' : (hasCode ? 'edit' : 'plus')}
                />
              </Button>
            )}
          </div>
        </div>

        {/* Mobile: Show code below header when collapsed */}
        {!isExpanded && hasCode && editable && (
          <div className='d-lg-none mt-2'>
            <small className='text-muted'>Code: </small>
            <code>{currentCode}</code>
          </div>
        )}

        {/* Expanded Content */}
        {(isExpanded || !editable) && (
          <div className='mt-3'>
            {/* Warning Alert */}
            {((strict_mode && editable) || (strict_mode && !editable && currentCode)) && (
              <Alert variant='warning' className='py-2 mb-3'>
                <small>
                  <FontAwesomeIcon icon='exclamation-triangle' className='mr-2' />
                  Alle oder keine Gruppenmitglieder erhalten einen Platz.
                </small>
              </Alert>
            )}

            {editable ? (
              <>
                {/* Input Field */}
                <Form.Group className='mb-2'>
                  <InputGroup>
                    <Form.Control
                      {...register('groupCode', { onBlur: handleCodeChange })}
                      type='text'
                      placeholder='z.B. Waffel-Zebra-7'
                      disabled={isDisabled}
                      isInvalid={!!errors.groupCode}
                      isValid={!!(hasCode && !errors.groupCode)}
                    />
                  </InputGroup>
                  <Form.Control.Feedback type='invalid'>
                    {errors.groupCode?.message}
                  </Form.Control.Feedback>
                  {isDisabled && (
                    <Form.Text className='text-muted'>
                      <small>Der Code kann nicht geändert werden.</small>
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Button Row - Responsive Layout */}
                <div className='d-flex gap-2'>
                  {/* Generate Button */}
                  <Button
                    variant='outline-primary'
                    className='flex-md-grow-1 mr-1' // Keep as is, or see note below
                    disabled={isDisabled || isGenerating}
                    onClick={handleGenerateCode}
                  >
                    {isGenerating ? (
                      <>
                        <span className='spinner-border spinner-border-sm mr-1' />
                        <span className='d-inline'>Generiere...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon='magic' className={"mr-1"} />
                        <span className='d-inline ms-1'>Generieren</span>
                      </>
                    )}
                  </Button>

                  {/* Share Button */}
                  <Dropdown>
                    <Dropdown.Toggle
                      variant='success'
                      disabled={!hasCode}
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '44px' }}
                    >
                      <FontAwesomeIcon icon='share-alt' className={"mr-1"} />
                      <span className='d-inline ms-1'>Teilen</span>
                    </Dropdown.Toggle>
                    <ShareButtons code={currentCode} registration={registration} />
                  </Dropdown>
                </div>
              </>
            ) : (
              /* Read-only Display */
              <div className='bg-light p-2 rounded'>
                {hasCode ? (
                  <div className='text-center'>
                    <small className='text-muted d-block'>Gruppencode:</small>
                    <code className='fs-6'>{registration.groupCode}</code>
                  </div>
                ) : (
                  <small className='text-muted'>Kein Gruppencode angegeben</small>
                )}
              </div>
            )}
          </div>
        )}
      </ListGroupItem>

      {/* Help Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon='users' className='me-2' />
            Freundesgruppen erklärt
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Mit Gruppencodes können sich Freunde oder Familie gemeinsam für Veranstaltungen anmelden.
          </p>

          <h6>So funktioniert's:</h6>
          <ol>
            <li>Einen <strong>eindeutigen Code</strong> erstellen (z.B. "Waffel-Zebra-7")</li>
            <li>Diesen Code mit Freunden teilen</li>
            <li>Alle verwenden <strong>denselben Code</strong> bei der Anmeldung</li>
            <li>Das System erkennt zusammengehörige Anmeldungen</li>
          </ol>

          <Card className='bg-light mt-3 mb-3'>
            <Card.Body className='py-2'>
              <strong>{strict_mode ? 'Alle oder niemand' : 'Gruppenwunsch'}</strong>
              <p className='mb-0 mt-1 small'>
                {strict_mode
                  ? 'Die gesamte Gruppe erhält nur dann Plätze, wenn für alle ein Platz verfügbar ist.'
                  : 'Eine gemeinsame Zuteilung wird angestrebt, kann aber nicht garantiert werden.'
                }
              </p>
            </Card.Body>
          </Card>

          <h6>Wichtige Hinweise</h6>
          <ul className='mb-0'>
            <li><strong>Eindeutige Codes</strong> verwenden</li>
            <li>Groß-/Kleinschreibung wird beachtet</li>
            <li>Der Code gilt nur für diese eine Veranstaltung</li>
            <li>Die Angabe ist vollkommen <strong>optional</strong></li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={() => setShowModal(false)}>
            Verstanden
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default GroupCode
